"use client";

import { useEffect, useState } from "react";
import Modal from "react-modal";
import { X, CircleUserIcon } from "lucide-react";
import { useGetUserStaff, useAddRelationManager } from "@/hooks/useCommon";
import { toast } from "react-toastify";

interface UserStatusModalProps {
  isOpen: boolean;
  userId: number;
  onClose: () => void;
}

const RelationManagerModal = ({
  isOpen,
  userId,
  onClose,
}: UserStatusModalProps) => {
  const {
    mutate: fetchUserStaff,
    data: userStaffData,
    isPending,
    isError,
  } = useGetUserStaff();

  const { mutate: addRelationShipManager, data } = useAddRelationManager();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nextApp = document.getElementById("__next");
      if (nextApp) Modal.setAppElement(nextApp);
    }
    if (userId > 0) {
      fetchUserStaff(
        { user_id: userId },
        {
          onSuccess: (data: any) => {
            if (data?.status == "success") {
              toast.info("User Staff data feteched.");
            }
          },
        }
      );
    }
  }, [userId]);

  const staffList = userStaffData?.data?.results || [];

  const handleAddClick = (staff_id: number) => {
    addRelationShipManager(
      { user_id: userId, staff_id: staff_id },
      {
        onSuccess: (data: any) => {
          if (data?.status == "success") {
            toast.success("Relationship manager added Succefully");
          }
        },
        onError: () => {
          toast.error("Something went wrong");
        },
      }
    );
  };
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0  bg-opacity-40 z-50 flex items-center justify-center"
      className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-hidden focus:outline-none flex flex-col"
      contentLabel="Relation Manager Modal"
    >
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <CircleUserIcon size={20} className="text-blue-500" />
            Relation Manager
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 transition cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1 min-h-0">
          <table className="min-w-full divide-y divide-gray-200 shadow rounded-sm">
            <thead className="bg-gray-800 text-white text-[12px] uppercase text-left">
              <tr>
                <th className="px-6 py-3">S.No</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Role</th>
                <th className="px-6 py-3">Department</th>
                <th className="px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 text-[12px] text-gray-700">
              {isPending && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              )}
              {!isPending && staffList.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    No staff data available for this vendor.
                  </td>
                </tr>
              )}
              {!isPending &&
                staffList.map((staff: any, index: number) => (
                  <tr key={staff.id || index}>
                    <td className="px-6 py-1">{index + 1}</td>
                    <td className="px-6 py-1">
                      {staff.first_name + staff.last_name}
                    </td>
                    <td className="px-6 py-1">{staff.rolename || "N/A"}</td>
                    <td className="px-6 py-1">
                      {staff.department_name || "N/A"}
                    </td>
                    <td className="px-6 py-1">
                      {staff.isDisabled ? (
                        <button
                          className="bg-gray-300 text-gray-600 px-3 py-1 rounded text-xs cursor-not-allowed"
                          disabled
                        >
                          N/A
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAddClick(staff.user_id)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 cursor-pointer"
                        >
                          Add
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-300 mt-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-1 rounded-lg text-sm transition"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default RelationManagerModal;
